import {
  Text,
  Box,
  Button,
  Icon,
  PopoverTrigger,
  PopoverContent,
  Popover,
} from '@tonic-one/react';
import { CheckIcon, CloseIcon, DownloadIcon } from '@tonic-one/react-icons';
import { memo, useState } from 'react';
import LoadingBtn from '@Components/Common/LoadingBtn';
import { OperationsApi } from '@Apis';
import { useTranslation } from 'react-i18next';
import { exportFile } from '@Utils/common';
import ActionModal from '@Components/Common/ActionModal';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
import { EuqFeedbackRecord, FeedbackRecordStatus } from '../type';
import { useEuqFeedbackMutations } from './hooks';
import { useOrgStore } from '@Models/useOrgModel';

export const DownloadBtn = memo(
  ({ selectedIds }: { selectedIds: string[] }) => {
    const { t } = useTranslation(['operations', 'common']);

    const onDownload = async () => {
      const res = await OperationsApi.apiDownloadEuqFeedBack(
        selectedIds.map((id) => {
          return {
            id,
          };
        })
      );
      const disposition = res.headers['content-disposition'];
      exportFile(
        disposition || 'mail.zip',
        res.data as any,
        'application/octet-stream'
      );
    };
    const disabled = selectedIds.length === 0 || selectedIds.length > 10;
    return (
      <Popover trigger="hover" placement={disabled ? 'top' : 'bottom'}>
        <PopoverTrigger>
          <Box>
            <Button disabled={disabled} variant="secondary" position="relative">
              <Icon as={DownloadIcon} mr="8px" />
              {t('common.action.label.download')}
            </Button>
          </Box>
        </PopoverTrigger>
        <PopoverContent maxWidth="304px">
          {disabled ? (
            t('operations.euq.detail.download.tip')
          ) : (
            <Box position="relative">
              <Text pb="40px">{t('operations.euq.tip.download')}</Text>
              <LoadingBtn
                disabled={disabled}
                variant="primary"
                onClick={onDownload}
                position="absolute"
                right="0"
                bottom="0"
              >
                {/* Download */}
                {(isLoading: boolean) => {
                  return isLoading
                    ? t('common.action.label.downloading')
                    : t('common.action.label.download');
                }}
              </LoadingBtn>
            </Box>
          )}
        </PopoverContent>
      </Popover>
    );
  }
);

export const ApproveBtn = ({
  selectedIds,
  refreshData,
  selectedItemsData = [],
}: {
  selectedIds: string[];
  refreshData: () => void;
  selectedItemsData?: EuqFeedbackRecord[];
}) => {
  const { orgId } = useOrgStore();
  const { widthEditablePermission } = usePermissionStore();
  const qtEditPermission = widthEditablePermission(
    PermissionNameEnum.quarantine
  );
  const { t } = useTranslation(['operations', 'common']);
  const [isOpen, setIsOpen] = useState(false);

  const { approveMutation } = useEuqFeedbackMutations({
    orgId,
    onSuccess: refreshData,
  });

  // Check if any selected items have already been acted upon
  const hasActedUponItems = selectedItemsData.some(
    (item) => item.status !== FeedbackRecordStatus.CREATED
  );

  const disabled =
    selectedIds.length === 0 ||
    selectedIds.length > 100 ||
    !qtEditPermission ||
    hasActedUponItems;

  const onApprove = async () => {
    try {
      await approveMutation.mutateAsync(selectedIds);
    } catch {
      // Error handling is already done in the mutation
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        disabled={disabled}
        variant="secondary"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <Icon as={CheckIcon} mr="8px" />
        {t('common.action.label.approve')}
      </Button>
      <ActionModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title={t('operations.euq.action.approve.title')}
        body={t('operations.euq.action.approve.desc')}
        confirmText={t('common.action.label.approve')}
        onConfirm={onApprove}
      />
    </>
  );
};

export const RejectBtn = ({
  selectedIds,
  refreshData,
  selectedItemsData = [],
}: {
  selectedIds: string[];
  refreshData: () => void;
  selectedItemsData?: EuqFeedbackRecord[];
}) => {
  const { orgId } = useOrgStore();
  const { widthEditablePermission } = usePermissionStore();
  const qtEditPermission = widthEditablePermission(
    PermissionNameEnum.quarantine
  );
  const { t } = useTranslation(['operations', 'common']);
  const [isOpen, setIsOpen] = useState(false);

  const { rejectMutation } = useEuqFeedbackMutations({
    orgId,
    onSuccess: refreshData,
  });

  // Check if any selected items have already been acted upon
  const hasActedUponItems = selectedItemsData.some(
    (item) => item.status !== FeedbackRecordStatus.CREATED
  );

  const disabled =
    selectedIds.length === 0 ||
    selectedIds.length > 100 ||
    !qtEditPermission ||
    hasActedUponItems;

  const onReject = async () => {
    try {
      await rejectMutation.mutateAsync(selectedIds);
    } catch {
      // Error handling is already done in the mutation
    } finally {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Button
        disabled={disabled}
        variant="secondary"
        onClick={() => {
          setIsOpen(true);
        }}
      >
        <Icon as={CloseIcon} mr="8px" />
        {t('common.action.label.reject')}
      </Button>
      <ActionModal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        title={t('operations.euq.action.reject.title')}
        body={t('operations.euq.action.reject.desc')}
        confirmText={t('common.action.label.reject')}
        onConfirm={onReject}
      />
    </>
  );
};
